import React, { useEffect, useState } from "react";
import { Flex, Text, Icon, useToast, Button } from "@chakra-ui/react";
import { motion, useAnimation } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { TREATMENTPLAN_SEND_EMAIL_RESET } from "../../../constants/treatmentPlanConstants";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  backgroundLight,
  buttonRedText,
  color3,
  primaryAlt,
  textPrimary,
  textSecondary,
  white,
} from "../../../utils/colors";
import { TbMailForward } from "react-icons/tb";

const MotionFlex = motion(Flex);

const IsSending = ({
  loadingSendEmail,
  successSendEmail,
  errorSendEmail,
  setOpen,
  isModalSending,
  setIsModalSending,
  fetchUserDetails,
  fetchUnavailable,
  setFetchUnavailable,
  closeHandler,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const [titleIndex, setTitleIndex] = useState(0);
  const [minDurationComplete, setMinDurationComplete] = useState(false); // To track when 3 seconds are complete
  const [displayCompletedState, setDisplayCompletedState] = useState(false); // This will control when to show the success or error state

  const titles = [
    t("treatmentPlan.isSendingStepOne"),
    t("treatmentPlan.isSendingStepTwo"),
    t("treatmentPlan.isSendingStepThree"),
  ];

  const animation = useAnimation();

  useEffect(() => {
    // Start the progress bar animation for at least 3 seconds
    animation.start({
      width: "100%",
      transition: { duration: 3, ease: "linear" },
    });

    // Update progress and title every second
    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 33.33, 99)); // Increment progress but keep it below 100%
      setTitleIndex((prev) => (prev + 1) % titles.length); // Change the title
    }, 1000);

    // Ensure the animation runs for at least 3 seconds
    const timeout = setTimeout(() => {
      setMinDurationComplete(true); // Mark minimum duration complete
      clearInterval(interval); // Stop the interval after the minimum duration
    }, 3000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [animation]);

  useEffect(() => {
    dispatch({ type: TREATMENTPLAN_SEND_EMAIL_RESET });
  }, []);

  useEffect(() => {
    setIsModalSending(true);
    // Ensure progress hits 100% once both success/error and minDurationComplete are true
    if ((successSendEmail || errorSendEmail) && minDurationComplete) {
      setProgress(100); // Complete the progress
      setDisplayCompletedState(true); // Only set this when both are true
      setIsModalSending(false);

      setFetchUnavailable(false);

      fetchUserDetails();

      // dispatch({ type: TREATMENTPLAN_SEND_EMAIL_RESET });
    }
  }, [successSendEmail, errorSendEmail, minDurationComplete]);

  const isCompleted = displayCompletedState; // Now this controls the final state

  // USE EFFECT

  // LOGS

  // console.log(
  //   "Is Sending:",
  //   loadingSendEmail,
  //   successSendEmail,
  //   errorSendEmail
  // );
  // console.log("Progress:", progress);
  // console.log("isCompleted:", isCompleted);
  // console.log("minDurationComplete:", minDurationComplete);

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      w="100%"
      minH="calc(100vh - 60px)"
      // minH="400px"
      // maxH="400px"
    >
      {!isCompleted ? (
        <>
          <Flex
            direction="column"
            align="center"
            justify="center"
            p="4rem 6rem"
            w="100%"
          >
            <Icon as={TbMailForward} fontSize="2.5rem" color={primaryAlt} />
            {/* Changing Title */}
            <Text
              fontSize="1.75rem"
              fontWeight="600"
              color={textPrimary}
              m="1rem 0 0.25rem"
            >
              {titles[titleIndex]}
            </Text>

            {/* Progress Bar */}
            <Flex
              w="100%"
              maxW="600px"
              h="8px"
              bg={backgroundLight}
              borderRadius="md"
              overflow="hidden"
              mt="1rem"
            >
              <MotionFlex
                h="8px"
                bg={primaryAlt}
                initial={{ width: 0 }}
                animate={animation}
                borderRadius="md"
              />
            </Flex>

            {/* Percentage Text */}
            <Text
              fontSize="lg"
              fontWeight="500"
              mt="0.75rem"
              color={textSecondary}
            >
              {Math.min(Math.floor(progress), 100)}%
            </Text>
          </Flex>
        </>
      ) : errorSendEmail ? (
        <Flex direction="column" align="center" justify="center" p="4rem 6rem">
          <Icon as={FaXmark} fontSize="3rem" color={buttonRedText} />
          <Text
            fontSize="1.75rem"
            fontWeight="600"
            color={textPrimary}
            m="1rem 0 0.25rem"
          >
            {t("treatmentPlan.sendingErrorTitle")}
          </Text>
          <Text
            fontSize="md"
            fontWeight="500"
            color={textSecondary}
            textAlign="center"
          >
            {t("treatmentPlan.sendingErrorDescription")}
          </Text>
          <Flex
            align="center"
            justify="center"
            w="100%"
            gap="0rem"
            m="2rem 0 0"
          >
            <Button
              minW="8rem"
              size="lg"
              fontSize="sm"
              bg={textPrimary}
              color={white}
              onClick={() => {
                setDisplayCompletedState(false);
                closeHandler();
              }}
              borderRadius="10rem"
              _hover={{ bg: primaryAlt, color: white }}
            >
              {t("button.close")}
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Flex
          direction="column"
          align="center"
          justify="center"
          p="3rem 6rem"
          h="100%"
        >
          <Icon as={FaCheckCircle} fontSize="2.5rem" color={color3} />
          <Text
            fontSize="1.75rem"
            fontWeight="600"
            color={textPrimary}
            m="1rem 0 0.25rem"
          >
            {t("treatmentPlan.sendingSuccessTitle")}
          </Text>
          <Text
            fontSize="md"
            fontWeight="500"
            color={textSecondary}
            textAlign="center"
          >
            {t("treatmentPlan.sendingSuccessDescription")}
          </Text>

          <Flex
            align="center"
            justify="center"
            w="100%"
            gap="0rem"
            m="2rem 0 0"
          >
            <Button
              minW="8rem"
              size="lg"
              fontSize="sm"
              bg={textPrimary}
              color={white}
              onClick={() => {
                closeHandler();
              }}
              // onClick={() => {
              //   setOpen(false);
              //   setDisplayCompletedState(false);
              // }}
              borderRadius="10rem"
              _hover={{ bg: primaryAlt, color: white }}
            >
              {t("button.close")}
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default IsSending;
